// src/hooks/useInstantListCache.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useInstantListCache
 * - lê cache 1x antes do 1º paint (zero flash)
 * - mostra cache instant
 * - faz fetch em background e atualiza + escreve cache
 * - evita double-fetch no React StrictMode (dev)
 */
export function useInstantListCache({
  cacheKey,
  fetcher, // async ({ signal }) => ({ items, meta? })
  initialItems = [],
  enabled = true,
  storage = "session", // "session" | "local"
}) {
  const store = storage === "local" ? window.localStorage : window.sessionStorage;

  const readCache = useCallback(() => {
    try {
      const raw = store.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [cacheKey, store]);

  // ✅ cache lida ANTES do 1º render
  const [cacheInit] = useState(() => (enabled ? readCache() : null));

  // ✅ estados já nascem preenchidos
  const [items, setItems] = useState(() => cacheInit?.items || initialItems);
  const [meta, setMeta] = useState(() => cacheInit?.meta || {});
  // ✅ não bloqueia o 1º paint (sem skeleton obrigatório)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const didFetchRef = useRef(false);

  const writeCache = useCallback(
    (nextItems, nextMeta) => {
      try {
        store.setItem(
          cacheKey,
          JSON.stringify({
            ts: Date.now(),
            items: nextItems,
            meta: nextMeta || {},
          })
        );
      } catch {
        // ignore quota / private mode
      }
    },
    [cacheKey, store]
  );

  const refresh = useCallback(async ({ showLoading = false } = {}) => {
    if (!enabled) return;

    const ac = new AbortController();
    setError(null);
    if (showLoading) setLoading(true);

    try {
      const res = await fetcher({ signal: ac.signal });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      const nextMeta = res?.meta || {};

      setItems(nextItems);
      setMeta(nextMeta);
      writeCache(nextItems, nextMeta);
    } catch (e) {
      if (String(e?.name) === "AbortError") return;
      setError(e);
    } finally {
      if (showLoading) setLoading(false);
    }

    return () => ac.abort();
  }, [enabled, fetcher, writeCache]);

  useEffect(() => {
    if (!enabled) return;

    if (didFetchRef.current) return;
    didFetchRef.current = true;

    const acRef = { abort: null };

    (async () => {
      // ✅ não bloqueia o paint; se quiseres spinner pequeno, mete showLoading: true
      const cleanup = await refresh({ showLoading: false });
      acRef.abort = cleanup;
    })();

    return () => {
      acRef.abort?.();
    };
  }, [enabled, refresh]);

  const api = useMemo(
    () => ({
      items,
      setItems, // útil p/ optimistic updates
      meta,
      setMeta,
      loading,
      error,
      refresh,
      hasCache: !!cacheInit,
      cacheTs: cacheInit?.ts || null,
    }),
    [items, meta, loading, error, refresh, cacheInit]
  );

  return api;
}
